import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { db, auth, functions } from '@/js/firebase'
import { logger } from '@/services/loggerService'
import { companyService } from '@/services/companyService'

/**
 * Storage tier definitions
 * These should match the settings/storageTiers document in Firestore
 */
export const STORAGE_TIERS = {
  basic: {
    name: 'Vysual Basic',
    storageBytes: 25000000000, // 25 GB (decimal — matches Bunny / cloud-storage convention)
    storagePretty: '25GB',
    pricePerMonth: 0,
    compareAtPrice: null,
    stripePriceId: null
  },
  plus: {
    name: 'Vysual+',
    storageBytes: 100000000000, // 100 GB
    storagePretty: '100GB',
    pricePerMonth: 99,
    compareAtPrice: null,
    stripePriceId: null
  },
  pro: {
    name: 'Vysual Pro',
    storageBytes: 500000000000, // 500 GB
    storagePretty: '500GB',
    pricePerMonth: 299,
    compareAtPrice: null,
    stripePriceId: null
  },
  max: {
    name: 'Vysual Max',
    storageBytes: 1000000000000, // 1 TB
    storagePretty: '1TB',
    pricePerMonth: 499,
    compareAtPrice: null,
    stripePriceId: null
  },
  // Vysual Platform — $99/mo + $0.20/GB metered plan. Mirrors
  // PLATFORM_TIER_DEFAULTS in functions/storage/platformPlan.js; the live
  // values come from settings/storageTiers.tiers.platform.
  platform: {
    name: 'Vysual Platform',
    pricePerMonth: 99,
    overageRatePerGB: 0.2,
    discountAfterGB: null, // volume discount: GB past this bill at discountRatePerGB
    discountRatePerGB: null,
    metered: true,
    storageBytes: 1000000000000, // allowance while a uvp/podcast contract qualifies
    storagePretty: '1TB',
    defaultExemptAllowanceBytes: 50000000000, // Nexus / agency-created companies
    stripePriceId: null,
    stripeTestPriceId: null,
    stripeMeteredPriceId: null,
    stripeTestMeteredPriceId: null
  }
}

export const PLATFORM_TIER = 'platform'

const BYTES_PER_GB = 1000 * 1000 * 1000

/** True for a subscription on the metered Vysual Platform plan. */
export const isPlatformSubscription = (subscription) => subscription?.tier === PLATFORM_TIER

/**
 * Platform allowance rule. MUST match resolvePlatformAllowance in
 * functions/storage/platformPlan.js — keep the two in step.
 *
 * Exempt (no platform fee) when qualified by a uvp/podcast contract or the
 * contract carries its own `allowanceBytes`; otherwise base plan, allowance 0.
 *
 * @param {Object} subscription - storage contract
 * @param {Object} [platformTier] - settings/storageTiers.tiers.platform
 * @returns {{ feeExempt: boolean, qualifies: boolean, allowanceBytes: number }}
 */
export function resolvePlatformAllowance(subscription, platformTier = STORAGE_TIERS.platform) {
  const qualifies = Boolean(subscription?.qualifiedBy)
  const own = subscription?.allowanceBytes ?? null
  const feeExempt = qualifies || own != null
  const qualifiedBytes = platformTier?.storageBytes ?? STORAGE_TIERS.platform.storageBytes
  const allowanceBytes = feeExempt ? Math.max(qualifies ? qualifiedBytes : 0, own ?? 0) : 0
  return { feeExempt, qualifies, allowanceBytes }
}

/** GB billed on the meter: everything above the allowance, rounded up. */
export function billableGB(currentBytes, allowanceBytes) {
  // Round to the byte first: averages carry float noise (mirrors platformPlan.js).
  return Math.max(0, Math.ceil(Math.round((currentBytes || 0) - (allowanceBytes || 0)) / BYTES_PER_GB))
}

/** Usage charge for billable GB — mirrors storageCharge in functions/storage/platformPlan.js. */
export function storageCharge(gb, platformTier = STORAGE_TIERS.platform) {
  const rate = platformTier?.overageRatePerGB ?? 0.2
  const after = platformTier?.discountAfterGB
  const discount = platformTier?.discountRatePerGB
  const units = Math.max(0, gb)
  if (!(after > 0) || !Number.isFinite(discount) || discount < 0) return +(units * rate).toFixed(2)
  return +(Math.min(units, after) * rate + Math.max(0, units - after) * discount).toFixed(2)
}

/** "$0.20/GB" or "$0.20/GB, $0.10/GB after 2 TB" — mirrors rateSummary server-side. */
export function rateSummary(platformTier = STORAGE_TIERS.platform) {
  const money = (n) => `$${Number(n).toFixed(2)}`
  const rate = platformTier?.overageRatePerGB ?? 0.2
  const after = platformTier?.discountAfterGB
  const discount = platformTier?.discountRatePerGB
  if (!(after > 0) || !Number.isFinite(discount) || discount < 0) return `${money(rate)}/GB`
  const size = after >= 1000 && after % 1000 === 0 ? `${after / 1000} TB` : `${after} GB`
  return `${money(rate)}/GB, ${money(discount)}/GB after ${size}`
}

export const STORAGE_ADDONS = {
  block_1tb: {
    name: '1TB Block',
    sizeBytes: 1000000000000, // 1 TB
    sizePretty: '1TB',
    pricePerMonth: 299,
    stripePriceId: null
  }
}

/**
 * Grace period durations in days
 */
const GRACE_PERIODS = {
  contractEnded: 30, // Days after production contract ends
  overage: 7 // Days after exceeding storage limit
}

class StorageSubscriptionService {
  constructor() {
    this.collection = 'contracts'
    this.tierConfig = null
  }

  /**
   * Load tier configuration from Firestore settings
   * Falls back to hardcoded values if not found
   */
  async loadTierConfig() {
    if (this.tierConfig) return this.tierConfig

    try {
      const docRef = doc(db, 'settings', 'storageTiers')
      const docSnap = await getDoc(docRef)

      if (docSnap.exists()) {
        this.tierConfig = docSnap.data()
        logger.info('[StorageSubscriptionService] Loaded tier config from Firestore')
      } else {
        // Use hardcoded defaults
        this.tierConfig = {
          tiers: STORAGE_TIERS,
          addOns: STORAGE_ADDONS,
          gracePeriods: GRACE_PERIODS
        }
        logger.info('[StorageSubscriptionService] Using default tier config')
      }

      return this.tierConfig
    } catch (error) {
      logger.error('[StorageSubscriptionService] Failed to load tier config', {
        error: error.message
      })
      // Return defaults on error
      return {
        tiers: STORAGE_TIERS,
        addOns: STORAGE_ADDONS,
        gracePeriods: GRACE_PERIODS
      }
    }
  }

  /**
   * Get tier options for display
   */
  async getTierOptions() {
    const config = await this.loadTierConfig()
    return {
      // The metered platform plan is never a pickable tier.
      tiers: Object.entries(config.tiers || STORAGE_TIERS)
        .filter(([, tier]) => !tier?.metered)
        .map(([key, tier]) => ({
          id: key,
          ...tier
        })),
      addOns: Object.entries(config.addOns || STORAGE_ADDONS).map(([key, addon]) => ({
        id: key,
        ...addon
      }))
    }
  }

  /**
   * Get storage subscription for a company
   * Returns the active storage subscription or null if none exists
   */
  async getSubscription(companyId) {
    try {
      const q = query(
        collection(db, this.collection),
        where('companyId', '==', companyId),
        where('type', '==', 'storage')
      )

      const querySnapshot = await getDocs(q)

      if (querySnapshot.empty) {
        logger.debug('[StorageSubscriptionService] No storage subscription found', { companyId })
        return null
      }

      // Should only be one storage subscription per company
      const docSnap = querySnapshot.docs[0]
      const data = docSnap.data()

      const subscription = {
        id: docSnap.id,
        ...data,
        // Convert Timestamps to milliseconds
        createdAt: data.createdAt?.toMillis ? data.createdAt.toMillis() : data.createdAt,
        updatedAt: data.updatedAt?.toMillis ? data.updatedAt.toMillis() : data.updatedAt,
        currentPeriodEnd: data.currentPeriodEnd?.toMillis ? data.currentPeriodEnd.toMillis() : data.currentPeriodEnd,
        currentPeriodStart: data.currentPeriodStart?.toMillis ? data.currentPeriodStart.toMillis() : (data.currentPeriodStart ?? null),
        gracePeriod: data.gracePeriod ? {
          ...data.gracePeriod,
          startedAt: data.gracePeriod.startedAt?.toMillis ? data.gracePeriod.startedAt.toMillis() : data.gracePeriod.startedAt,
          endsAt: data.gracePeriod.endsAt?.toMillis ? data.gracePeriod.endsAt.toMillis() : data.gracePeriod.endsAt
        } : null,
        usageStats: data.usageStats ? {
          ...data.usageStats,
          lastSyncedAt: data.usageStats.lastSyncedAt?.toMillis ? data.usageStats.lastSyncedAt.toMillis() : data.usageStats.lastSyncedAt,
          lastUpdatedAt: data.usageStats.lastUpdatedAt?.toMillis ? data.usageStats.lastUpdatedAt.toMillis() : data.usageStats.lastUpdatedAt
        } : null,
        qualifiedBy: data.qualifiedBy ? {
          ...data.qualifiedBy,
          qualifiedAt: data.qualifiedBy.qualifiedAt?.toMillis ? data.qualifiedBy.qualifiedAt.toMillis() : data.qualifiedBy.qualifiedAt
        } : null,
        adminOverride: data.adminOverride ? {
          ...data.adminOverride,
          grantedAt: data.adminOverride.grantedAt?.toMillis ? data.adminOverride.grantedAt.toMillis() : data.adminOverride.grantedAt
        } : null,
        suspension: data.suspension ? {
          ...data.suspension,
          suspendedAt: data.suspension.suspendedAt?.toMillis ? data.suspension.suspendedAt.toMillis() : data.suspension.suspendedAt
        } : null,
        discount: data.discount ? {
          ...data.discount,
          appliedAt: data.discount.appliedAt?.toMillis ? data.discount.appliedAt.toMillis() : data.discount.appliedAt,
          expiresAt: data.discount.expiresAt?.toMillis ? data.discount.expiresAt.toMillis() : data.discount.expiresAt
        } : null
      }

      logger.info('[StorageSubscriptionService] Fetched storage subscription', {
        companyId,
        subscriptionId: subscription.id,
        tier: subscription.tier
      })

      return subscription
    } catch (error) {
      logger.error('[StorageSubscriptionService] Failed to get subscription', {
        error: error.message,
        companyId
      })
      throw error
    }
  }

  /**
   * Create a new storage subscription for a company
   * Defaults to Basic (free) tier. For the platform tier pass
   * `options.allowanceBytes` (the company's own standing allowance; null =
   * base plan).
   */
  async createSubscription(companyId, tier = 'basic', qualifiedBy = null, options = {}) {
    try {
      const user = auth.currentUser
      if (!user) {
        throw new Error('User not authenticated')
      }

      // Check if subscription already exists
      const existing = await this.getSubscription(companyId)
      if (existing) {
        throw new Error('Storage subscription already exists for this company')
      }

      const config = await this.loadTierConfig()
      const tierConfig = config.tiers?.[tier] || STORAGE_TIERS[tier]

      if (!tierConfig) {
        throw new Error(`Invalid tier: ${tier}`)
      }

      const isPlatform = tier === PLATFORM_TIER
      // Platform: tierLimits mirrors the resolved allowance, same as the server's
      // createStorageSubscription — the tier's storageBytes is the 1 TB
      // qualifying allowance, not this account's.
      const limitBytes = isPlatform
        ? resolvePlatformAllowance({ allowanceBytes: options.allowanceBytes ?? null, qualifiedBy }, tierConfig).allowanceBytes
        : tierConfig.storageBytes
      const subscriptionData = {
        type: 'storage',
        companyId,
        active: true,
        tier,
        tierLimits: {
          storageBytes: limitBytes,
          storagePretty: isPlatform ? `${Math.round(limitBytes / 1e9)}GB` : tierConfig.storagePretty
        },
        ...(isPlatform ? { allowanceBytes: options.allowanceBytes ?? null } : {}),
        // Stripe fields - only populated when billing is needed
        stripeSubscriptionId: null,
        stripePriceId: null,
        stripeStatus: null,
        currentPeriodEnd: null,
        // Add-ons
        addOns: [],
        // Qualification
        qualifiedBy: qualifiedBy ? {
          contractId: qualifiedBy.contractId,
          contractType: qualifiedBy.contractType,
          qualifiedTier: tier,
          qualifiedAt: serverTimestamp()
        } : null,
        // Grace period
        gracePeriod: null,
        // Usage stats
        usageStats: {
          currentBytes: 0,
          lastSyncedAt: serverTimestamp(),
          lastUpdatedAt: serverTimestamp(),
          percentUsed: 0
        },
        // Audit fields
        createdAt: serverTimestamp(),
        createdBy: user.uid,
        updatedAt: serverTimestamp(),
        updatedBy: user.uid
      }

      // Same deterministic ID the server-side creators use, so a company can
      // only ever hold one storage contract. See
      // functions/storage/storageContractRef.js for why.
      const docRef = doc(db, this.collection, `storage_${companyId}`)
      await setDoc(docRef, subscriptionData)

      // Update company with reference to storage subscription
      await companyService.updateCompany(companyId, {
        storageSubscriptionId: docRef.id,
        storageSnapshot: {
          currentBytes: 0,
          limitBytes,
          percentUsed: 0,
          tier
        }
      })

      logger.info('[StorageSubscriptionService] Storage subscription created', {
        subscriptionId: docRef.id,
        companyId,
        tier,
        qualified: !!qualifiedBy
      })

      // Log activity
      await companyService.logActivity(
        companyId,
        'storage_subscription_created',
        `Storage Plan: ${tierConfig.name}`,
        null,
        null,
        qualifiedBy ? `Qualified via ${qualifiedBy.contractType} contract` : null
      )

      return await this.getSubscription(companyId)
    } catch (error) {
      logger.error('[StorageSubscriptionService] Failed to create subscription', {
        error: error.message,
        companyId,
        tier
      })
      throw error
    }
  }

  /**
   * Update storage subscription tier
   */
  async changeTier(subscriptionId, newTier) {
    try {
      const user = auth.currentUser
      if (!user) {
        throw new Error('User not authenticated')
      }

      const docRef = doc(db, this.collection, subscriptionId)
      const docSnap = await getDoc(docRef)

      if (!docSnap.exists()) {
        throw new Error('Storage subscription not found')
      }

      const currentData = docSnap.data()
      const oldTier = currentData.tier

      if (oldTier === newTier) {
        logger.debug('[StorageSubscriptionService] Tier unchanged', { subscriptionId, tier: newTier })
        return
      }

      const config = await this.loadTierConfig()
      const tierConfig = config.tiers?.[newTier] || STORAGE_TIERS[newTier]

      if (!tierConfig) {
        throw new Error(`Invalid tier: ${newTier}`)
      }

      const updateData = {
        tier: newTier,
        tierLimits: {
          storageBytes: tierConfig.storageBytes,
          storagePretty: tierConfig.storagePretty
        },
        updatedAt: serverTimestamp(),
        updatedBy: user.uid
      }

      // Recalculate percent used with new limit
      if (currentData.usageStats?.currentBytes) {
        updateData['usageStats.percentUsed'] =
          (currentData.usageStats.currentBytes / tierConfig.storageBytes) * 100
      }

      await updateDoc(docRef, updateData)

      // Update company snapshot
      await companyService.updateCompany(currentData.companyId, {
        'storageSnapshot.tier': newTier,
        'storageSnapshot.limitBytes': tierConfig.storageBytes,
        'storageSnapshot.percentUsed': updateData['usageStats.percentUsed'] || 0
      })

      logger.info('[StorageSubscriptionService] Tier changed', {
        subscriptionId,
        oldTier,
        newTier,
        companyId: currentData.companyId
      })

      // Log activity
      const oldTierConfig = config.tiers?.[oldTier] || STORAGE_TIERS[oldTier]
      await companyService.logActivity(
        currentData.companyId,
        'storage_tier_changed',
        'Storage Plan',
        oldTierConfig?.name || oldTier,
        tierConfig.name,
        null
      )

      return await this.getSubscription(currentData.companyId)
    } catch (error) {
      logger.error('[StorageSubscriptionService] Failed to change tier', {
        error: error.message,
        subscriptionId,
        newTier
      })
      throw error
    }
  }

  /**
   * Add a storage add-on to the subscription
   */
  async addAddon(subscriptionId, addonType) {
    try {
      const user = auth.currentUser
      if (!user) {
        throw new Error('User not authenticated')
      }

      const config = await this.loadTierConfig()
      const addonConfig = config.addOns?.[addonType] || STORAGE_ADDONS[addonType]

      if (!addonConfig) {
        throw new Error(`Invalid add-on type: ${addonType}`)
      }

      const docRef = doc(db, this.collection, subscriptionId)
      const docSnap = await getDoc(docRef)

      if (!docSnap.exists()) {
        throw new Error('Storage subscription not found')
      }

      const currentData = docSnap.data()
      const currentAddOns = currentData.addOns || []

      // Generate unique ID for this add-on instance
      const addonId = `${addonType}_${Date.now()}`

      const newAddon = {
        id: addonId,
        type: addonType,
        stripeSubscriptionItemId: null, // Set after Stripe checkout
        sizeBytes: addonConfig.sizeBytes,
        sizePretty: addonConfig.sizePretty,
        pricePerMonth: addonConfig.pricePerMonth,
        addedAt: Timestamp.now()
      }

      const updatedAddOns = [...currentAddOns, newAddon]

      // Calculate new effective limit
      const baseLimit = currentData.tierLimits?.storageBytes || 0
      const addonTotal = updatedAddOns.reduce((sum, addon) => sum + addon.sizeBytes, 0)
      const effectiveLimit = baseLimit + addonTotal

      await updateDoc(docRef, {
        addOns: updatedAddOns,
        updatedAt: serverTimestamp(),
        updatedBy: user.uid
      })

      // Update company snapshot with new effective limit
      const currentBytes = currentData.usageStats?.currentBytes || 0
      await companyService.updateCompany(currentData.companyId, {
        'storageSnapshot.limitBytes': effectiveLimit,
        'storageSnapshot.percentUsed': (currentBytes / effectiveLimit) * 100
      })

      logger.info('[StorageSubscriptionService] Add-on added', {
        subscriptionId,
        addonId,
        addonType,
        effectiveLimit
      })

      // Log activity
      await companyService.logActivity(
        currentData.companyId,
        'storage_addon_added',
        `Storage Add-on: ${addonConfig.name}`,
        null,
        null,
        `+${addonConfig.sizePretty} storage`
      )

      return await this.getSubscription(currentData.companyId)
    } catch (error) {
      logger.error('[StorageSubscriptionService] Failed to add add-on', {
        error: error.message,
        subscriptionId,
        addonType
      })
      throw error
    }
  }

  /**
   * Remove a storage add-on from the subscription
   */
  async removeAddon(subscriptionId, addonId) {
    try {
      const user = auth.currentUser
      if (!user) {
        throw new Error('User not authenticated')
      }

      const docRef = doc(db, this.collection, subscriptionId)
      const docSnap = await getDoc(docRef)

      if (!docSnap.exists()) {
        throw new Error('Storage subscription not found')
      }

      const currentData = docSnap.data()
      const currentAddOns = currentData.addOns || []

      const addonToRemove = currentAddOns.find(a => a.id === addonId)
      if (!addonToRemove) {
        throw new Error('Add-on not found')
      }

      const updatedAddOns = currentAddOns.filter(a => a.id !== addonId)

      // Calculate new effective limit
      const baseLimit = currentData.tierLimits?.storageBytes || 0
      const addonTotal = updatedAddOns.reduce((sum, addon) => sum + addon.sizeBytes, 0)
      const effectiveLimit = baseLimit + addonTotal

      await updateDoc(docRef, {
        addOns: updatedAddOns,
        updatedAt: serverTimestamp(),
        updatedBy: user.uid
      })

      // Update company snapshot
      const currentBytes = currentData.usageStats?.currentBytes || 0
      await companyService.updateCompany(currentData.companyId, {
        'storageSnapshot.limitBytes': effectiveLimit,
        'storageSnapshot.percentUsed': (currentBytes / effectiveLimit) * 100
      })

      logger.info('[StorageSubscriptionService] Add-on removed', {
        subscriptionId,
        addonId,
        effectiveLimit
      })

      // Log activity
      await companyService.logActivity(
        currentData.companyId,
        'storage_addon_removed',
        `Storage Add-on: ${addonToRemove.sizePretty}`,
        null,
        null,
        null
      )

      return await this.getSubscription(currentData.companyId)
    } catch (error) {
      logger.error('[StorageSubscriptionService] Failed to remove add-on', {
        error: error.message,
        subscriptionId,
        addonId
      })
      throw error
    }
  }

  /**
   * Update qualification status when production contract changes
   */
  async updateQualification(companyId, qualifiedBy) {
    try {
      const subscription = await this.getSubscription(companyId)
      if (!subscription) {
        // Create new subscription with qualification
        if (qualifiedBy) {
          const tier = this.getTierForContractType(qualifiedBy.contractType)
          return await this.createSubscription(companyId, tier, qualifiedBy)
        }
        return null
      }

      const user = auth.currentUser
      const docRef = doc(db, this.collection, subscription.id)

      if (qualifiedBy) {
        // Qualifying - upgrade tier and clear any grace period
        const qualifiedTier = this.getTierForContractType(qualifiedBy.contractType)
        const config = await this.loadTierConfig()
        const tierConfig = config.tiers?.[qualifiedTier] || STORAGE_TIERS[qualifiedTier]

        // Only upgrade if new tier is better
        const currentTierOrder = ['basic', 'plus', 'pro', 'max']
        const currentIndex = currentTierOrder.indexOf(subscription.tier)
        const qualifiedIndex = currentTierOrder.indexOf(qualifiedTier)

        if (qualifiedIndex > currentIndex) {
          await updateDoc(docRef, {
            tier: qualifiedTier,
            tierLimits: {
              storageBytes: tierConfig.storageBytes,
              storagePretty: tierConfig.storagePretty
            },
            qualifiedBy: {
              contractId: qualifiedBy.contractId,
              contractType: qualifiedBy.contractType,
              qualifiedTier,
              qualifiedAt: serverTimestamp()
            },
            gracePeriod: null,
            updatedAt: serverTimestamp(),
            updatedBy: user?.uid || 'system'
          })

          logger.info('[StorageSubscriptionService] Qualification upgraded tier', {
            companyId,
            oldTier: subscription.tier,
            newTier: qualifiedTier,
            contractType: qualifiedBy.contractType
          })
        } else {
          // Just update qualification info
          await updateDoc(docRef, {
            qualifiedBy: {
              contractId: qualifiedBy.contractId,
              contractType: qualifiedBy.contractType,
              qualifiedTier,
              qualifiedAt: serverTimestamp()
            },
            gracePeriod: null,
            updatedAt: serverTimestamp(),
            updatedBy: user?.uid || 'system'
          })
        }
      } else {
        // No longer qualified - start grace period
        const gracePeriodDays = GRACE_PERIODS.contractEnded
        const endsAt = new Date()
        endsAt.setDate(endsAt.getDate() + gracePeriodDays)

        await updateDoc(docRef, {
          qualifiedBy: null,
          gracePeriod: {
            type: 'contract_ended',
            startedAt: serverTimestamp(),
            endsAt: Timestamp.fromDate(endsAt),
            notificationsSent: [],
            previousTier: subscription.tier
          },
          updatedAt: serverTimestamp(),
          updatedBy: user?.uid || 'system'
        })

        logger.info('[StorageSubscriptionService] Started grace period', {
          companyId,
          tier: subscription.tier,
          gracePeriodDays,
          endsAt
        })
      }

      return await this.getSubscription(companyId)
    } catch (error) {
      logger.error('[StorageSubscriptionService] Failed to update qualification', {
        error: error.message,
        companyId
      })
      throw error
    }
  }

  /**
   * Set an admin override on a company's storage subscription
   * Grants a higher tier for free without requiring a production contract
   */
  async setAdminOverride(companyId, tier, reason) {
    try {
      const user = auth.currentUser
      if (!user) {
        throw new Error('User not authenticated')
      }

      if (!['plus', 'pro', 'max'].includes(tier)) {
        throw new Error(`Invalid override tier: ${tier}`)
      }

      const subscription = await this.getSubscription(companyId)
      if (!subscription) {
        throw new Error('No storage subscription found for this company')
      }

      const config = await this.loadTierConfig()
      const tierConfig = config.tiers?.[tier] || STORAGE_TIERS[tier]
      const docRef = doc(db, this.collection, subscription.id)

      // Only update tier/limits if override tier is higher than current
      const tierOrder = ['basic', 'plus', 'pro', 'max']
      const currentIndex = tierOrder.indexOf(subscription.tier)
      const overrideIndex = tierOrder.indexOf(tier)

      const updateData = {
        adminOverride: {
          tier,
          reason: reason || null,
          grantedAt: serverTimestamp(),
          grantedBy: user.uid
        },
        gracePeriod: null,
        updatedAt: serverTimestamp(),
        updatedBy: user.uid
      }

      if (overrideIndex > currentIndex) {
        updateData.tier = tier
        updateData.tierLimits = {
          storageBytes: tierConfig.storageBytes,
          storagePretty: tierConfig.storagePretty
        }
      }

      await updateDoc(docRef, updateData)

      // Update company snapshot if tier changed
      if (overrideIndex > currentIndex) {
        await companyService.updateCompany(companyId, {
          'storageSnapshot.tier': tier,
          'storageSnapshot.limitBytes': tierConfig.storageBytes
        })
      }

      logger.info('[StorageSubscriptionService] Admin override set', {
        companyId,
        tier,
        reason,
        grantedBy: user.uid
      })

      await companyService.logActivity(
        companyId,
        'storage_admin_override_set',
        `Admin Override: ${tierConfig.name}`,
        null,
        null,
        reason || null
      )

      return await this.getSubscription(companyId)
    } catch (error) {
      logger.error('[StorageSubscriptionService] Failed to set admin override', {
        error: error.message,
        companyId,
        tier
      })
      throw error
    }
  }

  /**
   * Remove an admin override from a company's storage subscription
   * Falls back to qualifiedBy tier or basic
   */
  async removeAdminOverride(companyId) {
    try {
      const user = auth.currentUser
      if (!user) {
        throw new Error('User not authenticated')
      }

      const subscription = await this.getSubscription(companyId)
      if (!subscription) {
        throw new Error('No storage subscription found for this company')
      }

      if (!subscription.adminOverride) {
        throw new Error('No admin override active for this company')
      }

      const docRef = doc(db, this.collection, subscription.id)
      const config = await this.loadTierConfig()

      // Determine fallback tier from qualifiedBy or default to basic
      let fallbackTier = 'basic'
      if (subscription.qualifiedBy?.qualifiedTier) {
        fallbackTier = subscription.qualifiedBy.qualifiedTier
      }

      const tierConfig = config.tiers?.[fallbackTier] || STORAGE_TIERS[fallbackTier]

      await updateDoc(docRef, {
        adminOverride: null,
        tier: fallbackTier,
        tierLimits: {
          storageBytes: tierConfig.storageBytes,
          storagePretty: tierConfig.storagePretty
        },
        updatedAt: serverTimestamp(),
        updatedBy: user.uid
      })

      // Update company snapshot
      await companyService.updateCompany(companyId, {
        'storageSnapshot.tier': fallbackTier,
        'storageSnapshot.limitBytes': tierConfig.storageBytes
      })

      logger.info('[StorageSubscriptionService] Admin override removed', {
        companyId,
        fallbackTier
      })

      await companyService.logActivity(
        companyId,
        'storage_admin_override_removed',
        'Admin Override',
        subscription.adminOverride.tier,
        fallbackTier,
        null
      )

      return await this.getSubscription(companyId)
    } catch (error) {
      logger.error('[StorageSubscriptionService] Failed to remove admin override', {
        error: error.message,
        companyId
      })
      throw error
    }
  }

  /**
   * Get the tier a contract type qualifies for
   */
  getTierForContractType(contractType) {
    switch (contractType) {
      case 'uvp':
        return 'max'
      case 'podcast':
      case 'episodes':
        return 'max'
      default:
        return 'basic'
    }
  }

  /**
   * Start overage grace period
   */
  async startOverageGracePeriod(subscriptionId) {
    try {
      const docRef = doc(db, this.collection, subscriptionId)
      const docSnap = await getDoc(docRef)

      if (!docSnap.exists()) {
        throw new Error('Storage subscription not found')
      }

      const currentData = docSnap.data()

      // Metered platform accounts never enter the overage path — going over
      // the allowance is billed, not blocked (plan §4).
      if (isPlatformSubscription(currentData)) {
        logger.debug('[StorageSubscriptionService] Platform plan — no overage grace', { subscriptionId })
        return
      }

      // Don't start if already in an active (non-expired) grace period.
      // Expired overage grace can be replaced by a fresh one — covers the
      // case where a prior grace ran out and was never cleared.
      const existing = currentData.gracePeriod
      const isReplaceableExpiredOverage =
        existing?.type === 'overage' && existing?.expired === true
      if (existing && !isReplaceableExpiredOverage) {
        logger.debug('[StorageSubscriptionService] Already in grace period', { subscriptionId })
        return
      }

      const gracePeriodDays = GRACE_PERIODS.overage
      const endsAt = new Date()
      endsAt.setDate(endsAt.getDate() + gracePeriodDays)

      await updateDoc(docRef, {
        gracePeriod: {
          type: 'overage',
          startedAt: serverTimestamp(),
          endsAt: Timestamp.fromDate(endsAt),
          notificationsSent: [],
          previousTier: currentData.tier
        },
        updatedAt: serverTimestamp()
      })

      logger.info('[StorageSubscriptionService] Started overage grace period', {
        subscriptionId,
        gracePeriodDays,
        endsAt
      })

      return await this.getSubscription(currentData.companyId)
    } catch (error) {
      logger.error('[StorageSubscriptionService] Failed to start overage grace period', {
        error: error.message,
        subscriptionId
      })
      throw error
    }
  }

  /**
   * Create Stripe checkout session for tier upgrade or add-on purchase
   * Only called when payment is actually needed
   */
  async createCheckoutSession(companyId, options) {
    try {
      // Use current page URL for success/cancel redirects if not provided
      const currentUrl = window.location.href.split('?')[0] // Remove any existing query params
      const successUrl = options.successUrl || `${currentUrl}?checkout=success`
      const cancelUrl = options.cancelUrl || `${currentUrl}?checkout=cancelled`

      const createCheckoutFn = httpsCallable(functions, 'createStorageCheckout')
      const result = await createCheckoutFn({
        companyId,
        ...options,
        successUrl,
        cancelUrl
      })

      logger.info('[StorageSubscriptionService] Checkout session created', {
        companyId,
        sessionId: result.data?.sessionId
      })

      return result.data
    } catch (error) {
      logger.error('[StorageSubscriptionService] Failed to create checkout session', {
        error: error.message,
        companyId,
        options
      })
      throw error
    }
  }

  /**
   * Start Stripe Checkout for the metered platform plan ("Add billing
   * information"). The server adds the flat $99 line only for base-plan
   * accounts; exempt accounts get the metered price alone.
   */
  async createPlatformCheckout(companyId, { successUrl, cancelUrl } = {}) {
    // Checkout fails failed-precondition without a Stripe customer, so make
    // sure one exists first (fails when the company has no primary contact).
    await this.ensureStripeCustomer(companyId, { throwOnError: true })
    return this.createCheckoutSession(companyId, { platform: true, successUrl, cancelUrl })
  }

  /**
   * Admin: set or clear a platform contract's own standing allowance.
   * `null` clears it → base plan ($99/mo) unless a uvp/podcast contract
   * qualifies it.
   * @param {string} companyId
   * @param {number|null} allowanceBytes
   */
  async updateAllowance(companyId, allowanceBytes) {
    try {
      const user = auth.currentUser
      if (!user) throw new Error('User not authenticated')

      if (allowanceBytes != null && (!Number.isFinite(allowanceBytes) || allowanceBytes < 0)) {
        throw new Error('Allowance must be a non-negative number of bytes')
      }

      const subscription = await this.getSubscription(companyId)
      if (!subscription) throw new Error('No storage subscription found')
      if (!isPlatformSubscription(subscription)) {
        throw new Error('Allowances only apply to the Vysual Platform plan')
      }

      const next = allowanceBytes == null ? null : Math.round(allowanceBytes)
      await updateDoc(doc(db, this.collection, subscription.id), {
        allowanceBytes: next,
        updatedAt: serverTimestamp(),
        updatedBy: user.uid
      })

      logger.info('[StorageSubscriptionService] Platform allowance updated', {
        companyId,
        previous: subscription.allowanceBytes ?? null,
        allowanceBytes: next,
        by: user.uid
      })

      await companyService.logActivity(
        companyId,
        'storage_allowance_updated',
        next == null
          ? 'Storage allowance cleared (base plan)'
          : `Storage allowance set to ${this.formatBytes(next)}`
      )

      return await this.getSubscription(companyId)
    } catch (error) {
      logger.error('[StorageSubscriptionService] Failed to update allowance', {
        error: error.message,
        companyId
      })
      throw error
    }
  }

  /**
   * Ensure Stripe customer exists for a company
   * Pre-creates the customer with email so checkout is faster
   * Call this proactively when loading storage tab
   */
  async ensureStripeCustomer(companyId, { throwOnError = false } = {}) {
    try {
      const ensureCustomerFn = httpsCallable(functions, 'ensureStripeCustomer')
      const result = await ensureCustomerFn({ companyId })

      logger.info('[StorageSubscriptionService] Stripe customer ensured', {
        companyId,
        customerId: result.data?.customerId,
        created: result.data?.created
      })

      return result.data
    } catch (error) {
      // Don't throw by default - this is a non-critical optimization
      logger.warn('[StorageSubscriptionService] Failed to ensure Stripe customer', {
        error: error.message,
        companyId
      })
      if (throwOnError) throw error
      return null
    }
  }

  /**
   * Get Stripe customer portal URL for managing billing
   * @param {string} companyId - Company ID
   * @param {string} returnUrl - Optional URL to return to after portal session (defaults to current page)
   */
  /**
   * Platform plan usage history: daily storage for the current billing period
   * plus past periods' averages and invoices (functions/storage/storageHistory.js).
   */
  async getUsageHistory(companyId, periodCount = 6) {
    const fn = httpsCallable(functions, 'getStorageUsageHistory')
    const result = await fn({ companyId, periodCount })
    return result.data
  }

  async getCustomerPortalUrl(companyId, returnUrl = null) {
    try {
      // Default to current page URL if not specified
      const finalReturnUrl = returnUrl || window.location.href.split('?')[0]

      const getPortalFn = httpsCallable(functions, 'getStorageCustomerPortal')
      const result = await getPortalFn({ companyId, returnUrl: finalReturnUrl })

      logger.info('[StorageSubscriptionService] Customer portal URL retrieved', { companyId })

      return result.data?.url
    } catch (error) {
      logger.error('[StorageSubscriptionService] Failed to get customer portal URL', {
        error: error.message,
        companyId
      })
      throw error
    }
  }

  /**
   * Calculate effective storage limit (tier + add-ons).
   *
   * When `tiers` (the live tier config) is provided, the base limit is derived
   * from the current config for the subscription's tier — mirroring the server's
   * usageSync. This keeps a subscription whose stored `tierLimits` is stale
   * (e.g. created before the tier's limit was raised) in sync with its tier.
   * Without `tiers`, it falls back to the subscription's stored `tierLimits`.
   *
   * @param {Object} subscription - Storage subscription document
   * @param {Object} [tiers] - Live tier config keyed by tier id (from loadTierConfig)
   */
  calculateEffectiveLimit(subscription, tiers = null) {
    if (!subscription) return 0

    // Platform: the allowance is the limit (0 on the base plan). Add-ons
    // don't apply. Mirrors the server's calculateEffectiveLimit.
    if (isPlatformSubscription(subscription)) {
      const platformTier = tiers?.platform ?? this.tierConfig?.tiers?.platform ?? STORAGE_TIERS.platform
      return resolvePlatformAllowance(subscription, platformTier).allowanceBytes
    }

    const baseLimit = tiers?.[subscription.tier]?.storageBytes
      ?? subscription.tierLimits?.storageBytes
      ?? 0
    const addonTotal = (subscription.addOns || [])
      .reduce((sum, addon) => sum + (addon.sizeBytes || 0), 0)

    return baseLimit + addonTotal
  }

  /**
   * Live platform tier config (falls back to the built-in defaults).
   */
  getPlatformTier() {
    return { ...STORAGE_TIERS.platform, ...(this.tierConfig?.tiers?.platform || {}) }
  }

  /**
   * Usage status for a subscription. Legacy tiers: normal/warning/critical/
   * overage by percent. Platform: never 'overage' — exempt accounts above
   * their allowance are 'billable'; the base plan is always 'normal'
   * (mirrors functions/storage/evaluateStorageState.js).
   */
  getUsageStatusFor(subscription, percentUsed) {
    if (isPlatformSubscription(subscription)) {
      const { feeExempt } = resolvePlatformAllowance(subscription, this.getPlatformTier())
      if (!feeExempt) return 'normal'
      if (percentUsed > 100) return 'billable'
      if (percentUsed >= 95) return 'critical'
      if (percentUsed >= 80) return 'warning'
      return 'normal'
    }
    if (percentUsed >= 100) return 'overage'
    if (percentUsed >= 95) return 'critical'
    if (percentUsed >= 80) return 'warning'
    return 'normal'
  }

  /**
   * Check if company is over their storage limit
   */
  isOverLimit(subscription) {
    if (!subscription) return false

    const effectiveLimit = this.calculateEffectiveLimit(subscription)
    const currentBytes = subscription.usageStats?.currentBytes || 0

    return currentBytes > effectiveLimit
  }

  /**
   * Get formatted storage amount
   */
  formatBytes(bytes) {
    if (bytes === 0) return '0 Bytes'

    // Decimal (SI) units: KB = 1000, GB = 10^9. Matches cloud-storage
    // convention (Bunny, AWS, GCP, Azure) so usage numbers line up with
    // what the upstream provider reports.
    const k = 1000
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  /**
   * Calculate effective price after discount
   * @param {Object} subscription - Storage subscription object
   * @returns {Object} { originalPrice, effectivePrice, discountAmount, discountLabel }
   */
  calculateEffectivePrice(subscription) {
    if (!subscription) {
      return { originalPrice: 0, effectivePrice: 0, discountAmount: 0, discountLabel: null }
    }

    // Platform: no fee when exempt (uvp/podcast or own allowance), else the
    // flat platform fee. Usage is billed separately and not included here.
    if (isPlatformSubscription(subscription)) {
      const platformTier = this.getPlatformTier()
      const originalPrice = platformTier.pricePerMonth ?? 99
      const { feeExempt } = resolvePlatformAllowance(subscription, platformTier)
      return feeExempt
        ? { originalPrice, effectivePrice: 0, discountAmount: 0, discountLabel: null, isQualified: true, feeExempt: true }
        : { originalPrice, effectivePrice: originalPrice, discountAmount: 0, discountLabel: null, isQualified: false, feeExempt: false }
    }

    const tier = subscription.tier || 'basic'
    const loadedTiers = this.tierConfig?.tiers || STORAGE_TIERS
    const tierData = loadedTiers[tier] || STORAGE_TIERS[tier]
    const originalPrice = tierData?.pricePerMonth || 0

    // If admin override is active, price is $0
    if (subscription.adminOverride) {
      return {
        originalPrice,
        effectivePrice: 0,
        discountAmount: originalPrice,
        discountLabel: 'Admin Override',
        isQualified: true
      }
    }

    // If qualified by contract, price is $0
    if (subscription.qualifiedBy) {
      const contractType = subscription.qualifiedBy.contractType
      let label = 'Contract'
      if (contractType === 'uvp') label = 'UVP Contract'
      else if (contractType === 'podcast') label = 'Podcast Contract'

      return {
        originalPrice,
        effectivePrice: 0,
        discountAmount: originalPrice,
        discountLabel: label,
        isQualified: true
      }
    }

    // If no discount, return original price
    if (!subscription.discount) {
      return {
        originalPrice,
        effectivePrice: originalPrice,
        discountAmount: 0,
        discountLabel: null,
        isQualified: false
      }
    }

    const { type, value, reason } = subscription.discount
    let effectivePrice = originalPrice
    let discountAmount = 0

    switch (type) {
      case 'percentage':
        discountAmount = Math.round(originalPrice * (value / 100))
        effectivePrice = originalPrice - discountAmount
        break
      case 'fixed':
        discountAmount = Math.min(value, originalPrice) // Can't discount more than the price
        effectivePrice = originalPrice - discountAmount
        break
      case 'custom':
        effectivePrice = value
        discountAmount = originalPrice - value
        break
      default:
        break
    }

    // Ensure price doesn't go negative
    effectivePrice = Math.max(0, effectivePrice)

    return {
      originalPrice,
      effectivePrice,
      discountAmount,
      discountLabel: reason || this.getDefaultDiscountLabel(type, value),
      isQualified: false
    }
  }

  /**
   * Get default discount label based on type and value
   */
  getDefaultDiscountLabel(type, value) {
    switch (type) {
      case 'percentage':
        return `${value}% off`
      case 'fixed':
        return `$${value} off`
      case 'custom':
        return 'Custom pricing'
      default:
        return 'Discount'
    }
  }

  /**
   * Apply a discount to a storage subscription
   * @param {string} subscriptionId - Subscription ID
   * @param {Object} discount - { type: 'percentage'|'fixed'|'custom', value: number, reason?: string, expiresAt?: Date }
   */
  async applyDiscount(subscriptionId, discount) {
    try {
      const user = auth.currentUser
      if (!user) {
        throw new Error('User not authenticated')
      }

      const docRef = doc(db, this.collection, subscriptionId)
      const docSnap = await getDoc(docRef)

      if (!docSnap.exists()) {
        throw new Error('Storage subscription not found')
      }

      const currentData = docSnap.data()

      // Validate discount type
      if (!['percentage', 'fixed', 'custom'].includes(discount.type)) {
        throw new Error(`Invalid discount type: ${discount.type}`)
      }

      // Validate value
      if (typeof discount.value !== 'number' || discount.value < 0) {
        throw new Error('Invalid discount value')
      }

      if (discount.type === 'percentage' && discount.value > 100) {
        throw new Error('Percentage discount cannot exceed 100%')
      }

      const discountData = {
        type: discount.type,
        value: discount.value,
        reason: discount.reason || null,
        appliedAt: serverTimestamp(),
        appliedBy: user.uid,
        expiresAt: discount.expiresAt ? Timestamp.fromDate(new Date(discount.expiresAt)) : null
      }

      await updateDoc(docRef, {
        discount: discountData,
        updatedAt: serverTimestamp(),
        updatedBy: user.uid
      })

      logger.info('[StorageSubscriptionService] Discount applied', {
        subscriptionId,
        discount: discountData,
        companyId: currentData.companyId
      })

      // Log activity
      const priceInfo = this.calculateEffectivePrice({
        ...currentData,
        discount: discountData
      })
      await companyService.logActivity(
        currentData.companyId,
        'storage_discount_applied',
        'Storage Discount',
        null,
        `${priceInfo.discountLabel} (now $${priceInfo.effectivePrice}/mo)`,
        discount.reason || null
      )

      return await this.getSubscription(currentData.companyId)
    } catch (error) {
      logger.error('[StorageSubscriptionService] Failed to apply discount', {
        error: error.message,
        subscriptionId
      })
      throw error
    }
  }

  /**
   * Remove discount from a storage subscription
   */
  async removeDiscount(subscriptionId) {
    try {
      const user = auth.currentUser
      if (!user) {
        throw new Error('User not authenticated')
      }

      const docRef = doc(db, this.collection, subscriptionId)
      const docSnap = await getDoc(docRef)

      if (!docSnap.exists()) {
        throw new Error('Storage subscription not found')
      }

      const currentData = docSnap.data()

      await updateDoc(docRef, {
        discount: null,
        updatedAt: serverTimestamp(),
        updatedBy: user.uid
      })

      logger.info('[StorageSubscriptionService] Discount removed', {
        subscriptionId,
        companyId: currentData.companyId
      })

      // Log activity
      await companyService.logActivity(
        currentData.companyId,
        'storage_discount_removed',
        'Storage Discount',
        'Removed',
        null,
        null
      )

      return await this.getSubscription(currentData.companyId)
    } catch (error) {
      logger.error('[StorageSubscriptionService] Failed to remove discount', {
        error: error.message,
        subscriptionId
      })
      throw error
    }
  }

  /**
   * Unlink Stripe subscription from a storage contract (admin only)
   * Only clears subscription-related fields from the contract, not the company's stripeCustomerId
   */
  async unlinkStripeBilling(companyId) {
    try {
      const user = auth.currentUser
      if (!user) {
        throw new Error('User not authenticated')
      }

      const subscription = await this.getSubscription(companyId)
      if (!subscription) {
        throw new Error('No storage subscription found for this company')
      }

      const docRef = doc(db, this.collection, subscription.id)

      // Clear Stripe subscription fields from contract only
      await updateDoc(docRef, {
        stripeSubscriptionId: null,
        stripeCustomerId: null,
        stripePriceId: null,
        stripeStatus: null,
        currentPeriodEnd: null,
        updatedAt: serverTimestamp(),
        updatedBy: user.uid
      })

      logger.info('[StorageSubscriptionService] Stripe subscription unlinked', {
        subscriptionId: subscription.id,
        companyId
      })

      // Log activity
      await companyService.logActivity(
        companyId,
        'storage_subscription_unlinked',
        'Stripe Subscription',
        'Linked',
        'Unlinked',
        'Stripe subscription disconnected by admin'
      )

      return await this.getSubscription(companyId)
    } catch (error) {
      logger.error('[StorageSubscriptionService] Failed to unlink Stripe subscription', {
        error: error.message,
        companyId
      })
      throw error
    }
  }

  /**
   * Delete storage subscription (admin only)
   */
  async deleteSubscription(subscriptionId) {
    try {
      const docRef = doc(db, this.collection, subscriptionId)
      const docSnap = await getDoc(docRef)

      if (!docSnap.exists()) {
        throw new Error('Storage subscription not found')
      }

      const data = docSnap.data()

      await deleteDoc(docRef)

      // Clear company reference
      await companyService.updateCompany(data.companyId, {
        storageSubscriptionId: null,
        storageSnapshot: null
      })

      logger.info('[StorageSubscriptionService] Storage subscription deleted', {
        subscriptionId,
        companyId: data.companyId
      })
    } catch (error) {
      logger.error('[StorageSubscriptionService] Failed to delete subscription', {
        error: error.message,
        subscriptionId
      })
      throw error
    }
  }
  /**
   * Toggle suspension on/off for a company's storage subscription
   */
  async setSuspension(companyId, suspended) {
    try {
      const user = auth.currentUser
      if (!user) throw new Error('User not authenticated')

      const subscription = await this.getSubscription(companyId)
      if (!subscription) throw new Error('No storage subscription found')

      const docRef = doc(db, this.collection, subscription.id)

      if (suspended) {
        await updateDoc(docRef, {
          suspension: {
            suspended: true,
            suspendedAt: serverTimestamp(),
            suspendedBy: user.uid,
            reason: 'admin_manual',
            exempt: subscription.suspension?.exempt || false
          },
          updatedAt: serverTimestamp(),
          updatedBy: user.uid
        })
      } else {
        // Preserve exempt flag when unsuspending
        const exempt = subscription.suspension?.exempt || false
        await updateDoc(docRef, {
          suspension: exempt ? { suspended: false, exempt: true } : null,
          updatedAt: serverTimestamp(),
          updatedBy: user.uid
        })
      }

      logger.info('[StorageSubscriptionService] Suspension toggled', {
        companyId,
        suspended,
        by: user.uid
      })

      await companyService.logActivity(
        companyId,
        suspended ? 'storage_suspended' : 'storage_unsuspended',
        suspended ? 'Account suspended by admin' : 'Account suspension lifted by admin'
      )

      return await this.getSubscription(companyId)
    } catch (error) {
      logger.error('[StorageSubscriptionService] Failed to set suspension', {
        error: error.message,
        companyId,
        suspended
      })
      throw error
    }
  }

  /**
   * Toggle auto-suspension exemption for a company
   */
  async setSuspensionExempt(companyId, exempt) {
    try {
      const user = auth.currentUser
      if (!user) throw new Error('User not authenticated')

      const subscription = await this.getSubscription(companyId)
      if (!subscription) throw new Error('No storage subscription found')

      const docRef = doc(db, this.collection, subscription.id)

      if (subscription.suspension) {
        // Update existing suspension object
        await updateDoc(docRef, {
          'suspension.exempt': exempt,
          updatedAt: serverTimestamp(),
          updatedBy: user.uid
        })
      } else {
        // Create suspension object with only exempt flag
        await updateDoc(docRef, {
          suspension: { suspended: false, exempt },
          updatedAt: serverTimestamp(),
          updatedBy: user.uid
        })
      }

      logger.info('[StorageSubscriptionService] Suspension exemption toggled', {
        companyId,
        exempt,
        by: user.uid
      })

      await companyService.logActivity(
        companyId,
        exempt ? 'storage_suspension_exempt_set' : 'storage_suspension_exempt_removed',
        exempt ? 'Company exempted from auto-suspension' : 'Auto-suspension exemption removed'
      )

      return await this.getSubscription(companyId)
    } catch (error) {
      logger.error('[StorageSubscriptionService] Failed to set suspension exempt', {
        error: error.message,
        companyId,
        exempt
      })
      throw error
    }
  }
}

export const storageSubscriptionService = new StorageSubscriptionService()
